/**
 * AIKeyDialog
 *
 * Dialog for adding/editing an AI provider API key.
 * Validates the key before saving.
 *
 * @module ai-settings/components
 */

import React, { useState } from 'react';
import {
  TextField,
  Alert,
  CircularProgress,
  Typography,
  Box,
} from '@mui/material';
import { SDialog } from '@modules/ui-components/dialogs';
import type { AIProviderInfo, AIProviderType } from '../types/aiKey.types';

interface AIKeyDialogProps {
  open: boolean;
  onClose: () => void;
  provider: AIProviderInfo;
  onSave: (provider: AIProviderType, apiKey: string) => Promise<void>;
  onValidate: (provider: AIProviderType, apiKey: string) => Promise<boolean>;
  saving: boolean;
  validating: boolean;
}

export const AIKeyDialog: React.FC<AIKeyDialogProps> = ({
  open,
  onClose,
  provider,
  onSave,
  onValidate,
  saving,
  validating,
}) => {
  const [apiKey, setApiKey] = useState('');
  const [validated, setValidated] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setApiKey('');
    setValidated(null);
    setError(null);
    onClose();
  };

  const handleValidate = async () => {
    if (!apiKey.trim()) return;
    setError(null);
    setValidated(null);
    try {
      const isValid = await onValidate(provider.type, apiKey.trim());
      setValidated(isValid);
      if (!isValid) {
        setError('Key validation failed. Please check your API key.');
      }
    } catch (err: any) {
      setError(err.message || 'Validation failed');
      setValidated(false);
    }
  };

  const handleSave = async () => {
    if (!apiKey.trim()) return;
    setError(null);
    try {
      await onSave(provider.type, apiKey.trim());
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save key');
    }
  };

  const isLoading = saving || validating;

  return (
    <SDialog
      open={open}
      onClose={handleClose}
      title={`Add ${provider.name} API Key`}
      maxWidth="sm"
      fullWidth
      actions={[
        {
          label: 'Cancel',
          onClick: handleClose,
          variant: 'text' as const,
        },
        {
          label: validating ? 'Validating...' : 'Validate',
          onClick: handleValidate,
          variant: 'outlined' as const,
          disabled: !apiKey.trim() || isLoading,
        },
        {
          label: saving ? 'Saving...' : 'Save',
          onClick: handleSave,
          variant: 'contained' as const,
          disabled: !apiKey.trim() || isLoading,
        },
      ]}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {provider.description}. Your key is stored securely in Google Cloud Secret Manager.
        </Typography>

        <TextField
          label="API Key"
          type="password"
          fullWidth
          value={apiKey}
          onChange={(e) => {
            setApiKey(e.target.value);
            setValidated(null);
            setError(null);
          }}
          placeholder={provider.keyPlaceholder}
          disabled={isLoading}
          autoFocus
          InputProps={{
            endAdornment: isLoading ? <CircularProgress size={20} /> : null,
          }}
        />

        {validated === true && (
          <Alert severity="success">Key is valid</Alert>
        )}
        {error && (
          <Alert severity="error">{error}</Alert>
        )}
      </Box>
    </SDialog>
  );
};
